import { useEffect, useState } from 'react';
import { ChevronRight } from 'react-feather';
import { Link } from 'react-router-dom';

import Pagination from '@/shared/components/UI/Pagination';
import { useSecondaryAuthGuard } from '@/shared/hooks/useAuthGuard';
import apiToken, { handleApiError } from '@/shared/services/axiosInstance';
import { getCurrencySymbol } from '@/shared/utils/currencyUtils';
import { formatDateKo } from '@/shared/utils/dateUtils';
import { formatPrice } from '@/shared/utils/priceUtils';

import { OrderItem, OrderListResponse } from '../types/OrderTypes';

const MyShoppingInfo = () => {
  const [orderList, setOrderList] = useState<OrderItem[]>([]);
  const [totalCount, setTotalCount] = useState(1);

  const { isVerified } = useSecondaryAuthGuard();

  const getShoppingInfo = async () => {
    try {
      const response =
        await apiToken.get<OrderListResponse>('/api/v1/order/list');
      setOrderList(response.data.data.orderList);
      setTotalCount(Math.ceil(response.data.data.totalCount / 4));
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to fetch shopping info:', errorMessage);
      alert('Failed to load your order history. Please try again in a moment.');
    }
  };

  useEffect(() => {
    if (isVerified) {
      getShoppingInfo();
    }
  }, [isVerified]);

  return (
    <div>
      {isVerified && (
        <section className="mx-auto">
          <div className="w-full max-w-[48rem]">
            <h2 className="text-center text-[1.25rem] font-black text-[#95699f]">
              Shopping Info
            </h2>
            <hr className="mx-auto my-[24px] mb-[32px] border border-[#ccc]" />
            {orderList.length === 0 && (
              <p className="w-[720px]">No orders found.</p>
            )}
            {orderList.map((item) => {
              const firstProduct = item.orderDetails[0];
              const additionalItemsCount = item.orderDetails.length - 1;
              const thumbnailUrl =
                firstProduct?.productOption.thumbnailUrlArray[0] ||
                './images/head.jpg';

              return (
                <div
                  key={item.uuid}
                  className="mx-auto mb-4 w-[720px] rounded-xl bg-white p-6 shadow-[7px_8px_14px_#22222208]"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <p className="text-[14px] text-[#222]">
                      {formatDateKo(item.createdAt)} | {item.orderNumber}
                    </p>
                    <Link to={`/mypage/shoppingdetail/${item.uuid}`}>
                      <ChevronRight
                        size={20}
                        color="#666"
                        strokeWidth={1.5}
                        className="cursor-pointer"
                      />
                    </Link>
                  </div>
                  <div className="flex gap-4">
                    <img
                      src={thumbnailUrl}
                      alt="product"
                      className="h-[100px] w-[100px] rounded object-cover"
                    />
                    <div className="shopping-contact-text flex flex-col justify-between">
                      <div>
                        <h4 className="w-[480px] truncate text-[16px] font-semibold text-[#222]">
                          {firstProduct?.productOption.productTitle}
                        </h4>
                        {additionalItemsCount > 0 && (
                          <span className="mt-3 inline-block text-[14px] text-[#999]">
                            and {additionalItemsCount} more
                          </span>
                        )}
                      </div>
                      <p className="text-[16px] font-semibold text-[#95699f]">
                        {formatPrice(
                          item.finalPaymentAmount,
                          item.currencyCode,
                        )}{' '}
                        {getCurrencySymbol(item.currencyCode)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            <Pagination totalCount={totalCount} />
          </div>
        </section>
      )}
    </div>
  );
};

export default MyShoppingInfo;
